import { createSlice } from '@reduxjs/toolkit';
import { EditorState } from 'draft-js';
import { v4 } from 'uuid';

const initialState = {
    activeEditorInstance: "editorInstanceOne",
    editorInstancesIds: ["editorInstanceOne"],
    editorInstancesDelta: [],
    editorInstances: {},
    docTitle: "Untitled Pad",
    editorRef: null,
    editorState: EditorState.createEmpty(),
    documentInformation: {},
    isReadOnly: false,
    userInformation: {},
    mouse: [],
    isExport: '',
    isFileChoose: false,
    file: {},
    pages: [{
        pageId: `page-${v4()}`,
        editorState: EditorState.createEmpty(),
        isActive: true,
    }],
    signatures: [],
    savingData: {
        isSaving: "",
        lastSavedDateTime: "",
        fileSize: 0
    },
    documentVersionData: {
        isVersionChange: false,
        versionData: {},
        originalData: {
            editorData: '',
            summary: ''
        }
    },
    lexicalEditor: null,
    editorSummary: '',
    isExternal: false,
    isExternalAllow: false,
    isChangeByUser: false,
    isConflictModalVisible: false,
    serverData: {},
    conflictPreviewVerion: null,
    isAuthenticated: false,
    isTemplateDocument: null,
    templateData: {},
    modals: {
        share: false,
        createPad: false,
        version: false,
        selectedTemplate: null,
    },

}

const editorSlice = createSlice({
    name: 'editor',
    initialState,
    reducers: {
        pushEditorInstanceId: (state, actions) => {
            const { editorInstanceId } = actions.payload;
            state.editorInstancesIds.push(editorInstanceId);
            state.activeEditorInstance = editorInstanceId;
        },
        setDocumentInformation(state, action) {
            state.documentInformation = action.payload;
            const searchParams = new URLSearchParams(document.location.search)
            const isReadOnlyQuery = searchParams.get("readOnly")
            if (isReadOnlyQuery == "true" || action.payload.rightType == 1) {
                state.isReadOnly = true;
            }
        },
        setUserInformation(state, action) {
            state.userInformation = action.payload;
        },
        setMouse(state, action) {
            state.mouse = action.payload;
        },
        insertEditorInstancesDelta: (state, action) => {
            state.editorInstancesDelta = action.payload
        },
        updateActiveEditor: (state, actions) => {
            const { activeEditorInstance } = actions.payload;
            state.activeEditorInstance = activeEditorInstance;
        },
        setEditorRef: (state, action) => {
            state.editorRef = action.payload;
        },
        updateEditorState: (state, action) => {
            state.editorState = action.payload;
        },
        updateIsExport: (state, action) => {
            state.isExport = action.payload
        },
        updateIsFileChoose: (state, action) => {
            state.isFileChoose = action.payload
        },
        chooseFile: (state, action) => {
            state.file = action.payload
        },
        addPage: (state, action) => {
            state.pages.push(action.payload);
        },
        updatePage: (state, action) => {
            const { pageId, editorState } = action.payload;
            const page = state.pages.find(p => p.pageId === pageId);
            if (page) {
                page.editorState = editorState;
            }
        },
        deletePage: (state, action) => {
            state.pages = state.pages.filter(page => page.pageId !== action.payload.pageId);
        },
        setActivePage: (state, action) => {
            const { pageId } = action.payload;
            state.pages = state.pages.map(page => ({
                ...page,
                isActive: page.pageId === pageId
            }));
        },
        loadSignatures(state, action) {
            state.signatures = [...action.payload];
        },
        addSignature(state, action) {
            state.signatures.push(action.payload);
        },
        updateSignature(state, action) {
            const { signatureIndex, signatureData } = action.payload;
            if (state.signatures[signatureIndex]) {
                state.signatures[signatureIndex] = { ...signatureData };
            }
        },
        updateIsSaving: (state, action) => {
            state.savingData = {
                ...action.payload
            }
        },
        updatePages(state, action) {
            state.pages = [...action.payload];
        },
        setIsReadOnly(state, action) {
            state.isReadOnly = action.payload;
        },
        saveDocumnetVersionData(state, action) {
            const { isVersionChange, updatedVersionData, originalData } = action.payload;
            state.documentVersionData.isVersionChange = isVersionChange;
            state.documentVersionData.versionData = updatedVersionData;
            state.documentVersionData.originalData = originalData;
        },
        setReadOnly(state, action) {
            state.isReadOnly = action.payload;
        },
        setLexicalEditor(state, action) {
            state.lexicalEditor = action.payload;
        },
        setEditorSummary(state, action) {
            state.editorSummary = action.payload;
        },
        removeDocumentMember(state, action) {
            state.documentInformation.members = state.documentInformation?.members?.filter((member) => member.id !== action.payload);
        },
        setIsExternal(state, action) {
            state.isExternal = action.payload;
        },
        setIsExternalAllow(state, action) {
            state.isExternalAllow = action.payload;
        },
        setIsChangeByUser(state, action) {
            state.isChangeByUser = action.payload;
        },
        setIsConflictModalVisible(state, action) {
            state.isConflictModalVisible = action.payload;
        },
        setServerData(state, action) {
            state.serverData = action.payload;
        },
        setConflictPreviewVerionstate(state, action) {
            state.conflictPreviewVerion = action.payload;
        },
        setIsAuthenticated(state, action) {
            state.isAuthenticated = action.payload;
        },
        setIsTemplateDocument(state, action) {
            state.isTemplateDocument = action.payload;
        },
        setTemplateData(state, action) {
            state.templateData = action.payload;
        },
        openShareModal(state) {
            state.modals.share = true;
        },
        closeShareModal(state) {
            state.modals.share = false;
        },
        openCreatePadDrawer(state, action) {
            state.modals.createPad = true;
            state.modals.selectedTemplate = action.payload;
        },
        closeCreatePadDrawer(state) {
            state.modals.createPad = false;
            state.modals.selectedTemplate = null;
        },
        openVersionModal(state) {
            state.modals.version = true;
        },
        closeVersionModal(state) {
            state.modals.version = false;
        },
    },
})

export const {
    pushEditorInstanceId,
    insertEditorInstancesDelta,
    updateActiveEditor,
    setEditorRef,
    updateEditorState,
    setDocumentInformation,
    setUserInformation,
    setMouse,
    updateIsFileChoose,
    updateIsExport,
    chooseFile,
    fetchFileContent,
    addPage,
    updatePage,
    deletePage,
    setActivePage,
    loadSignatures,
    addSignature,
    updateSignature,
    updateIsSaving,
    updatePages,
    setIsReadOnly,
    saveDocumnetVersionData,
    setReadOnly,
    setLexicalEditor,
    setEditorSummary,
    removeDocumentMember,
    setIsExternal,
    setIsExternalAllow,
    setIsChangeByUser,
    setIsConflictModalVisible,
    setServerData,
    setConflictPreviewVerionstate,
    setIsAuthenticated,
    setIsTemplateDocument,
    setTemplateData,
    openShareModal,
    closeShareModal,
    openCreatePadDrawer,
    closeCreatePadDrawer,
    openVersionModal,
    closeVersionModal,

} = editorSlice.actions;

export default editorSlice.reducer